/**
 * constants.ts — Single source of truth for every tunable gameplay value.
 *
 * Want to change the difficulty or feel of the game? This is the ONLY file you
 * should need to touch. Each value is grouped and commented with what it does
 * and which direction makes the game harder or easier.
 *
 * Units: distances are in world units, speeds in units/second, durations in
 * milliseconds unless noted otherwise.
 *
 * COORDINATES: gameplay runs in 2D `(x, y)` on the flat ground. The renderer maps
 * world `(x, y)` → Three.js `(x, height, y)`, i.e. gameplay **y becomes world z**.
 * So every value here is a ground-plane measurement; `*_HEIGHT` values are the
 * only vertical (Three.js y) ones.
 */

import {
  WALLS,
  ROOFS,
  PAINTS,
  GLASS,
  DOORS,
  GROUND_COLORS,
  MASONRY,
  ATMOSPHERE,
  CHARACTERS,
  CHARACTER_DARK,
  FOLIAGE,
  PROPS,
  FX,
} from './palette';
import type { CritterReaction, HazardType } from '../types';

/** Ground-plane extent of the level, and the sky/fog backdrop. */
export const WORLD = {
  WIDTH: 960,
  HEIGHT: 640,
  BACKGROUND_COLOR: ATMOSPHERE.SKY,
  // Fog distances are measured from the *camera*, so they are tied to
  // CAMERA.DISTANCE: pull the camera back without moving these and the haze
  // creeps down the screen toward the walker. Both carry the factor the camera
  // was last pulled back by (540 → 660 units), which holds every point on screen
  // at the fraction of the fog band it was at before. `constants.test.ts` keeps
  // the walker in clear air and the frame corners under a haze ceiling.
  FOG_NEAR: 860,
  FOG_FAR: 1830,
  /**
   * World units in one metre — the scale everything in the town was modelled
   * at (a life-size brick is 2 units, a front door 15, a two-lane road 64).
   *
   * Nothing in the simulation needs it: gameplay is in units end to end. It is
   * here for the one place a *number* is shown to the player — the home
   * pointer's readout — because a raw unit count labelled "m" reads as ten
   * times the distance it is, and put the far side of the street at 160m.
   */
  UNITS_PER_METRE: 10,
} as const;

/** Tilted follow camera. Higher TILT = closer to straight-down. */
export const CAMERA = {
  FOV: 50,
  TILT_DEG: 58, // angle below horizontal; 90 would be straight down
  /**
   * How far back/up the camera sits from the player (↑ = zoomed out).
   *
   * This is a difficulty knob as much as a look. Traffic is the one hazard that
   * arrives from off screen, and a car crosses the visible half-width in
   * `DISTANCE × tan(hFOV/2) / VEHICLES.SPEED` seconds — so the zoom level *is*
   * the reaction time you get to dodge. 540 gave about 2.6s on a 16:9 screen,
   * which read as cars appearing already on top of you; 660 gives about 3.2s,
   * enough to see one coming and pick a side.
   *
   * Pulling back sends the top of the frame further into the fog, so
   * `WORLD.FOG_NEAR`/`FOG_FAR` scale with this — see the note there, and
   * `constants.test.ts` → "the fog sits behind what the camera can see".
   */
  DISTANCE: 660,
  FOLLOW_LERP: 0.09, // 0..1 per frame smoothing (↑ = snappier follow)
  LOOK_AHEAD: 40, // shifts the focus point slightly ahead of the player
} as const;

/**
 * Scene lighting. A hemisphere light does the outdoor sky/ground fill, and one
 * directional "sun" casts the shadows that sell the depth.
 *
 * SUN_OFFSET is the sun's position *relative to the centre of the map*. Keep it
 * high (large y vs. x/z) — a low sun throws building shadows right across the
 * level and drowns the whole city in shade.
 */
export const LIGHTING = {
  SKY_COLOR: ATMOSPHERE.SKY_LIGHT,
  GROUND_BOUNCE_COLOR: ATMOSPHERE.GROUND_BOUNCE,
  // Bright ambient fill with a softer sun on top: the look is an overcast-but-
  // pleasant English day, not hard midday shadow. Pushing SUN_INTENSITY up and
  // HEMI down gives a harsher, more Mediterranean scene.
  //
  // Both are higher than they'd need to be for a Lambert scene. The clay
  // materials are `MeshStandardMaterial` at high roughness, which returns
  // noticeably less light than Lambert did for the same rig — without the lift
  // the whole town reads as an overcast dusk. The hemisphere carries most of it
  // on purpose: a big soft fill against a gentle sun is what gives clay its
  // wide, weak shadows, which matters more here now that the shadow map itself
  // can't blur them (see render/scene.ts).
  HEMI_INTENSITY: 2.6,
  SUN_COLOR: ATMOSPHERE.SUN,
  SUN_INTENSITY: 1.9,
  SUN_OFFSET: { x: -320, y: 900, z: -300 }, // ≈64° elevation
  SHADOW_MAP_SIZE: 2048,
  // Offsets the shadow lookup along the surface normal. Rounded clay presents a
  // lot of near-grazing surface, which is where shadow acne lives; ↓ and fine
  // streaks appear across the ground, ↑ too far and contact shadows detach from
  // whatever is casting them.
  SHADOW_NORMAL_BIAS: 0.6,
  // Output exposure. The clay materials carry a little emissive of their own,
  // so this sits slightly under 1 to stop the pale renders blowing out.
  EXPOSURE: 0.95,
} as const;

/**
 * Touch / pointer controls — a floating virtual stick, driven by the same code
 * for a finger and a held mouse button (see core/Input.ts, systems/joystick.ts).
 *
 * Distances are **screen pixels**, not world units: they describe a gesture, so
 * they're sized for thumbs rather than for the level.
 */
export const TOUCH = {
  // Drag distance meaning "full speed". ↑ = must reach further to sprint.
  // Sized so a thumb can reach it without shifting grip.
  MAX_RADIUS: 70,
  // Slop before the player moves at all, so a resting thumb doesn't drift.
  DEAD_ZONE: 10,
  // Presses in the top slice of the screen don't start the stick, keeping the
  // HUD readouts tappable-without-walking. Everything below is fair game.
  ZONE_TOP_FRACTION: 0.18,
  // Visual sizes of the ring and its knob, in CSS pixels.
  BASE_SIZE: 132,
  KNOB_SIZE: 58,
} as const;

/** Player. RADIUS is the ground-plane collision circle; HEIGHT is vertical. */
export const PLAYER = {
  RADIUS: 16,
  HEIGHT: 46,
  COLOR: CHARACTERS.SHIRT,
  SKIN_COLOR: CHARACTERS.SKIN,
  // Collar, sleeve bands and hem — the ribbing on a tee, and the only thing
  // stopping it reading as one flat red slab from directly above.
  SHIRT_DEEP_COLOR: CHARACTERS.SHIRT_DEEP,
  TROUSERS_COLOR: CHARACTERS.TROUSERS,
  HAIR_COLOR: CHARACTERS.HAIR,
  BOOT_COLOR: CHARACTERS.BOOT,
  EYE_COLOR: CHARACTERS.EYE,
  SPEED: 220, // walking speed. ↑ = easier to resist the dog's pull
  // Half-extent of the *model* (18-wide torso, arms swinging out to ~13), as
  // opposed to RADIUS above. See DOG.BODY_RADIUS for why the two differ.
  BODY_RADIUS: 14,
  // The walker and the dog are solid to each other — they can't stand in the same
  // spot. When they overlap, this is the share of the separation the *walker*
  // absorbs; the dog takes the rest. ↓ = the walker shoulders the dog aside
  // rather than being nudged off course (the walker is the heavier of the two).
  DOG_PUSH_SHARE: 0.15,
} as const;

/** The dog and the elastic leash connecting it to the player. */
export const DOG = {
  RADIUS: 14,
  HEIGHT: 22,
  // Nose-to-tail body length, matching the torso box in render/models.ts (and the
  // RADIUS collision circle). The leash is sized as a multiple of this — see
  // LEASH.MAX_LENGTH — so a bigger dog gets a proportionally longer lead.
  LENGTH: 28,
  // Half-extent of the *model*, which is much longer than it is wide: nose to
  // tail is ~46 (torso 26, plus head/snout forward and tail back). RADIUS above
  // is the gameplay circle used for car and critter hit tests, and it's far too
  // small to keep the models apart — separating the walker and dog on RADIUS
  // alone still lets a head-on dog bury its snout in the walker's coat. So the
  // walker↔dog body separation uses BODY_RADIUS on both sides instead.
  BODY_RADIUS: 24,
  COLOR: CHARACTERS.DOG,
  // Darker tone for ears/legs/tail plus the saddle down its back, and a pale
  // tone for muzzle, blaze, socks, chest and tail tip. Seen from straight above,
  // a one-colour rounded dog is a featureless loaf — these are what put a head
  // and four legs back into the silhouette. See palette.ts → CHARACTERS.
  DARK_COLOR: CHARACTERS.DOG_DARK,
  PALE_COLOR: CHARACTERS.DOG_PALE,
  NOSE_COLOR: CHARACTERS.DOG_NOSE,
  EYE_COLOR: CHARACTERS.EYE,
  COLLAR_COLOR: PROPS.COLLAR,
  COLLAR_TAG_COLOR: PROPS.COLLAR_TAG,
  // Base wander speed while calm — the dog drifts gently around the player.
  CALM_SPEED: 70,
  // Speed while sniffing out a spot, just before it needs the grass. Slower
  // than calm on purpose: the dog dawdling and nosing at the pavement is the
  // first thing the player feels through the lead, before any HUD line.
  SNIFF_SPEED: 45,
  // Pull speed when DISTRACTED (spotted something mildly interesting).
  DISTRACTED_SPEED: 180,
  // ── The lunge, as a range rather than a single speed ────────────────────────
  // A dog locked onto a critter pulls harder the closer the critter is: the
  // speed is interpolated between these two by the proximity the HazardManager
  // measures (see systems/frenzy.ts → `critterPull`). LUNGE_MIN_SPEED is what a
  // critter at the very edge of HAZARD.HYPER_PULL_RADIUS is worth; HYPER_SPEED
  // is the full yank, at nose-to-nose. Keep MIN at or above DISTRACTED_SPEED or
  // spotting a cat across the street would *calm the dog down*.
  LUNGE_MIN_SPEED: 190,
  // Pull speed when HYPER_PULL and the critter is right there. The big yank.
  HYPER_SPEED: 340,
  // ── Barking ────────────────────────────────────────────────────────────────
  // The dog barks the whole time it is lunging, and the gap between barks
  // closes as the critter gets nearer — the same proximity that sets the pull
  // above, so what you hear and what you feel are one signal. MIN is the gap at
  // nose-to-nose, MAX the gap at the edge of the radius.
  BARK_MIN_MS: 260,
  BARK_MAX_MS: 1100,
} as const;

/** How long the lead is, measured in dog-lengths. The one dial for leash reach. */
const LEASH_DOG_LENGTHS = 2.5;

/**
 * Leash spring (Hooke's law, velocity-space) — see systems/Leash.ts for the math.
 * The leash is a damped spring. Once the player↔dog distance exceeds REST_LENGTH,
 * a restoring pull proportional to how far it is stretched drags the two together.
 * We express the pull directly as a velocity contribution (px/s) so tuning is in
 * intuitive pixel-speed units and the result is rock-stable (no runaway bounce).
 */
export const LEASH = {
  // A short lead: the dog stays at heel rather than ranging half a street away.
  // The whole budget is tight, so the three distances have to be read together —
  // the dog lives between the body-separation gap and the hard cap:
  //
  //   38 ─────────── 50 ──────────── 70
  //   bodies touch   leash goes      hard cap
  //   (BODY_RADIUS   taut            (2.5 dog-lengths)
  //    sum)          (REST_LENGTH)   (MAX_LENGTH)
  //   └── slack ──┘  └─── stretch ───┘
  //
  // Shorten MAX_LENGTH without raising the others and the slack band vanishes,
  // leaving the lead permanently taut.
  DOG_LENGTHS: LEASH_DOG_LENGTHS, // leash length, expressed in dog-lengths
  REST_LENGTH: 50, // slack length; no pull is applied within this radius
  // Hard cap; beyond this the dog is clamped so it can't run off.
  MAX_LENGTH: DOG.LENGTH * LEASH_DOG_LENGTHS,
  // Spring stiffness "k": pull speed (px/s) added per pixel the leash is stretched.
  // ↑ = a snappier, harder pull (harder game). Short leash, stiff spring: there's
  // only ~20px of stretch to work with, so each pixel has to count — at full
  // stretch this yields ~300 px/s of drag, comparable to the dog's hyper-pull.
  // (Safe to crank: the pull is a velocity, not an acceleration, so it can't
  // accumulate into a runaway bounce however stiff it gets.)
  STIFFNESS: 15,
  // Caps the leash pull so an extreme stretch can't fling the player. (px/s)
  MAX_PULL_SPEED: 360,
  // Damping softens the pull by the closing speed already happening along the
  // leash, so it settles instead of jittering. 0 = springy, 1 = very stiff/dead.
  DAMPING: 0.25,
  // ── Who wins a tug of war ──────────────────────────────────────────────────
  // The walker is the heavier of the two, so the same tension moves them less
  // than it moves the dog. These are the fractions of the pull each one feels.
  // Keep PLAYER < DOG or the dog starts winning.
  PLAYER_MASS_FACTOR: 0.45, // ↑ = the dog drags the walker around more (harder)
  DOG_MASS_FACTOR: 1.0, // ↑ = the dog is reined in harder (easier)
  // Hard floor on the walker's own agency: however hard the dog hauls, the
  // walker still makes at least this fraction of their intended progress in the
  // direction they're steering. So the dog can make a direction *slow and
  // awkward* but never impossible, and can never walk the walker backwards.
  // 0 = the dog can stall you outright, 1 = the dog can't slow you at all.
  MIN_PLAYER_PROGRESS: 0.3,
  // Both are red — the lead keeps its identity across the whole range, and the
  // strain shows as the red *brightening* rather than as a change of colour.
  // What drives it is the dog's lunge (systems/frenzy.ts), not a measurement of
  // the cord: the lead is the loudest thing on screen that can say "your dog
  // has seen something", so it says it.
  SLACK_COLOR: PROPS.LEASH_SLACK, // deep red with the dog at heel
  TAUT_COLOR: PROPS.LEASH_TAUT, // bright red at a full lunge (lerps between the two)
  // Vertical (Three.js y) attach points. These are the walker's hand and the
  // dog's collar as *modelled* — the lead visibly leaves a fist and clips to a
  // collar, so moving either mesh means moving these to match.
  HAND_HEIGHT: 21,
  COLLAR_HEIGHT: 18,
  // ── How the lead is drawn (see systems/Leash.ts) ───────────────────────────
  // It's a rolled clay cord, not a line. A `THREE.Line` is one pixel wide at any
  // distance and any thickness — WebGL ignores `linewidth` — so the lead all but
  // disappeared at the gameplay camera despite being the central mechanic.
  THICKNESS: 2.2, // cord diameter. ↑ reads as a rope, ↓ back toward a thread
  // How far the middle of the cord droops when the dog is at heel, easing to
  // nothing as it lunges. A second, spatial reading of the same number the
  // frenzy meter shows: a drooping lead means nothing has caught the dog's eye.
  SAG: 9,
  // Straight pieces the sag curve is chopped into. ↑ = smoother arc, more meshes.
  SEGMENTS: 8,
} as const;

/**
 * The clay look (see render/clay.ts, which is the only place these are used).
 *
 * Everything in the game is modelled as if pressed out of Play-doh: rounded off,
 * matte, and very slightly wonky. Purely cosmetic — none of this touches
 * collision, which still uses the square rects and circles in the groups above.
 */
export const CLAY = {
  // Corner radius as a fraction of an object's smallest dimension. This is the
  // main dial for the whole look. ↓0.1 = crisp toy bricks, ↑0.45 = soft pebbles
  // (and shapes start losing their silhouette). Capped at just under half the
  // smallest side in clay.ts — at exactly half, the geometry inverts.
  ROUND: 0.3,
  // Subdivisions across each rounded corner. 1 is a visible chamfer, 3 reads as
  // properly round, past 4 is wasted vertices at this camera distance.
  SEGMENTS: 3,
  // Below this smallest-side length a block drops to a 1-segment chamfer. Sills,
  // glazing bars and door furniture are only a few pixels across in play but are
  // the most numerous meshes in the town, so they dominate the triangle budget
  // if given the full treatment. ↑ = cheaper and crisper-edged small detail.
  DETAIL_MIN: 4,
  SPHERE_SEGMENTS: 12,
  // Rounding on a pressed-out shape (clayExtrude). Absolute rather than a
  // fraction, because these are outlines rather than blocks — an arch head and
  // a gable end are wildly different sizes and want the same soft edge.
  EXTRUDE_BEVEL: 0.9,
  // Matte surface. Clay has almost no specular — drop this much below 0.8 and it
  // starts looking like polished plastic.
  ROUGHNESS: 0.9,
  // Faint self-glow in each object's own colour, standing in for light creeping
  // through real clay. Small: this is the difference between shadowed sides
  // staying coloured and going flat grey, but ↑0.2 and everything looks lit
  // from within like a lampshade.
  SUBSURFACE: 0.1,
  // Hand-placed wobble, in radians (~0.6° tilt, ~1.4° yaw). Rotation only, so
  // nothing drifts off its collision rect. ↑ = charmingly shonky, then drunk.
  WOBBLE: 0.011,
  WOBBLE_YAW: 0.025,
} as const;

/** Visual flourishes (see render/effects.ts). Purely cosmetic — no gameplay effect. */
export const EFFECTS = {
  MAX_PARTICLES: 240, // pool ceiling; oldest is recycled past this
  GRAVITY: 420, // world units/s² pulling particles back down
  POOP_COLOR: FX.POOP,
  SPARKLE_COLOR: FX.SPARKLE,
  /** The ring that fills while the dog is going — brown, so it reads as its own thing. */
  POOP_PROGRESS_COLOR: FX.POOP_PROGRESS,
  FRENZY_COLOR: FX.FRENZY,
  BIN_COLOR: FX.BIN_POP,
  CONFETTI_COLORS: FX.CONFETTI,
  /**
   * How high the flat, ground-hugging effects are drawn.
   *
   * The town's walkable surfaces are **not all at ground level**: a footway is a
   * kerb-height block with paving on top (`PAVEMENT.KERB_HEIGHT + 0.1`), and a
   * park sits on its own low kerb. A ring drawn at "the ground" is therefore
   * drawn *inside* the pavement the moment the pair step onto it, and the
   * cleanup meter — which is a gameplay readout, not a flourish — disappears
   * exactly where most of the walking happens.
   *
   * So everything flat is lifted clear of the tallest surface anyone can stand
   * on. At this camera tilt the couple of units of float is invisible; the ring
   * vanishing is not. `constants.test.ts` pins that this stays above the
   * pavement if either number is ever tuned.
   */
  GROUND_CLEARANCE: 6,
  /** How long the dog's pose takes to ease between states (per-frame factor). */
  POSE_LERP: 0.12,
} as const;

/**
 * Procedural walk animation. There are no spritesheets in 3D — limbs are rotated
 * directly, with the cycle phase advanced in proportion to how fast the entity is
 * actually moving, so walking speed and leg speed stay in sync.
 */
export const ANIM = {
  // Radians of phase advanced per world-unit travelled. ↑ = faster leg churn.
  PHASE_PER_UNIT: 0.09,
  PLAYER_LEG_SWING: 0.7, // max leg rotation (radians)
  PLAYER_ARM_SWING: 0.55,
  PLAYER_BOB: 1.6, // vertical body bob (world units)
  DOG_LEG_SWING: 0.6,
  DOG_BOB: 1.0,
  DOG_TAIL_WAG: 0.5, // tail sweep amplitude (radians)
  DOG_TAIL_HZ: 6, // tail wags per second while calm
  TURN_LERP: 0.2, // how quickly a body yaws toward its travel direction
  // Critters bound rather than glide: hop arc height and how much they squash
  // on landing (0 = rigid, 1 = fully flattened).
  HAZARD_HOP_HEIGHT: 7,
  HAZARD_SQUASH: 0.22,
  // Pigeon wings. The flap is fast and shallow in the air and reduced to a
  // twitch on the ground — a bird that keeps beating its wings while walking
  // reads as broken, and one that holds them dead still reads as a statue.
  WING_FLAP_HZ: 9,
  WING_FLAP: 0.85, // radians swept per beat
  // How far out the wings are held *before* the beat is added. Without it the
  // bottom of every stroke folds the wings flat against the body, and a bird
  // photographed at that instant is a bird sitting on the pavement — which is
  // half the frames of a flight that is only a second long.
  WING_SPREAD: 0.42,
  WING_FOLD: 0.12, // radians of idle shuffle while walking
} as const;

/** Dog state-machine timing. */
export const DOG_STATE = {
  // Base time the dog stays calm before it might get distracted, plus a random
  // jitter added on top. ↓ interval = more frequent chaos (harder).
  CALM_MIN_MS: 2500,
  CALM_JITTER_MS: 3500,
  // How long a distraction lasts before the dog calms back down.
  DISTRACTED_MS: 1800,
  // How close (px) the DISTRACTED target is picked from the dog's position.
  DISTRACT_TARGET_RADIUS: 200,
  // Calm wander: the dog picks a spot in this ring around the player. The inner
  // radius must clear the body-separation gap (PLAYER.BODY_RADIUS +
  // DOG.BODY_RADIUS = 38) — the two collide now, so a target inside the walker
  // just has the dog shoving at their shins. The outer radius must stay inside
  // LEASH.MAX_LENGTH, or a "calm" dog spends the whole walk straining the lead.
  WANDER_MIN_RADIUS: 44,
  WANDER_MAX_RADIUS: 64,
  // Earliest / latest time (from level start) the dog will need to poop.
  POOP_MIN_MS: 8000,
  POOP_JITTER_MS: 9000,
  // How long before that the dog starts sniffing about — the player's warning
  // that a park is about to be needed. Long enough to cross a street or two and
  // act on it, short enough that "soon" still means soon. Must stay under
  // POOP_MIN_MS, or the walk would open with the dog already restless.
  SNIFF_WARNING_MS: 4000,
  // How long the dog spends actually going, once it is standing on the grass.
  // The act used to be instantaneous — the poop simply appeared under a dog
  // that never visibly did anything — so this is long enough to read as a
  // deliberate squat (and to fill the ring drawn around it) without stranding
  // the player waiting. The dog is rooted for the whole of it.
  //
  // Doubled from 2800 when the cleanup hold was merged into it (see `types.ts`
  // → `Objective`). The hold was three seconds of standing still bolted onto
  // this one, and taking it out took the whole pause down to under three — so
  // the squat carries the beat now rather than being the shorter half of it.
  // It is also the walk's one moment of *enforced* stillness, which is the
  // interesting part: the dog is rooted, the lead is an anchor, and a car or a
  // cat arriving during it is something you have to have thought about before
  // you picked the park.
  POOP_DURATION_MS: 5600,
} as const;

/** Hazards: roaming cats, foxes, squirrels, and pigeons. */
export const HAZARD = {
  SIZE: 26, // ground-plane footprint (also the collision diameter)
  HEIGHT: 16,
  // Placeholder colors — see render/models.ts to swap primitives for real models.
  COLOR: {
    cat: CHARACTERS.CAT,
    fox: CHARACTERS.FOX,
    squirrel: CHARACTERS.SQUIRREL,
    pigeon: CHARACTERS.PIGEON,
  },
  /** Darker tone for each critter's ears, paws, and tail tip. */
  DARK_COLOR: CHARACTER_DARK,
  COUNT: { cat: 2, fox: 2, squirrel: 3, pigeon: 4 },
  /**
   * What each species does about the dog. See `CritterReaction` in types.ts for
   * what the four answers mean; the behaviour is in entities/Hazard.ts.
   *
   * Every critter used to freeze-and-bolt, which made them one animal in four
   * coats: whichever one you walked into, it ran, and there was nothing to
   * learn about any of them. These are the differences.
   */
  REACTION: {
    cat: 'nerve',
    fox: 'hold',
    squirrel: 'flee',
    pigeon: 'flush',
  } as Record<HazardType, CritterReaction>,
  SPEED_MIN: 40,
  SPEED_MAX: 110,
  // How often each hazard picks a new random heading.
  RETARGET_MIN_MS: 1200,
  RETARGET_JITTER_MS: 2200,
  // If any critter comes within this range of the dog → HYPER_PULL, at a
  // strength that rises the closer it gets (systems/frenzy.ts → `critterPull`).
  // ↑ = the dog reacts from farther away (harder).
  HYPER_PULL_RADIUS: 140,
  // Startle: how close the dog must get before a critter reacts, how long the
  // whole reaction lasts, how much of that is the frozen "anticipation" beat, and
  // how fast it then bolts away.
  STARTLE_RADIUS: 95,
  STARTLE_MS: 1400,
  STARTLE_FREEZE_MS: 260,
  STARTLE_SPEED: 210,
  // How close the dog has to get before a critter that stands its ground breaks
  // and runs anyway — the cat's rule ('nerve'). Comfortably inside
  // STARTLE_RADIUS, so a cat visibly holds first and *then* bolts: at the same
  // radius the hold would never be seen and the cat would just be a squirrel.
  PANIC_RADIUS: 44,
  // Pigeons: how far a bark carries, how long the flight lasts, how fast they
  // go, and how high they get.
  //
  // The radius is wider than HYPER_PULL_RADIUS on purpose. The dog only barks
  // at something within that range, so a matching radius would flush *only* the
  // bird being barked at; a bark that lifts the whole square is the thing
  // everyone has actually seen happen.
  BARK_FLUSH_RADIUS: 210,
  FLIGHT_MS: 1250,
  FLIGHT_SPEED: 260,
  // Apex of the flight arc, in world units of altitude. High enough to clear
  // the walker and read as *over* the street rather than along it.
  FLIGHT_HEIGHT: 78,
} as const;

/**
 * Frenzy: how wound up the dog is, and the one thing that can end a walk other
 * than a car.
 *
 * The bar is fed by exactly the same number as the lunge and the barking — how
 * close the nearest critter is to the dog — so everything the player can see
 * and hear is one signal read three ways: the lead goes tight and bright, the
 * barking speeds up, and the meter climbs. Fill it and the dog is gone.
 *
 * The rates are the whole difficulty of the mechanic. RISE is per second *at a
 * full-strength lunge* and scales down with the pull, so it takes
 * `1 / RISE_PER_SEC` seconds of nose-to-nose to lose — long enough to haul the
 * dog away, short enough that standing next to a cat is not a plan. DECAY is
 * flat and deliberately faster than the rise: getting clear should visibly buy
 * the bar back, or the meter becomes a countdown rather than a tug of war.
 */
export const FRENZY = {
  RISE_PER_SEC: 0.2, // 5s of nose-to-nose critter to fill from empty
  DECAY_PER_SEC: 0.32, // ~3s of clear air to empty it again
  // Grace after the critter leaves before the bar starts falling, so shaking a
  // cat for a fraction of a second isn't a free reset.
  CALM_DELAY_MS: 450,
  // Where the HUD starts shouting about it (meter flashes, wording changes).
  WARN_AT: 0.7,
} as const;

/**
 * The guided walk: how the tutorial's lessons are paced.
 *
 * The *script* — which lessons there are and what finishes each one — is the
 * pure `systems/tutorial.ts`; the wording is in `ui/hud.ts` with every other
 * line the player reads. These are the numbers behind it, and they are the same
 * wherever a guided walk is played, which is why they live here rather than on
 * the level (see `systems/levelgen/tutorial.ts` for what *that* level varies).
 */
export const TUTORIAL = {
  // How far the walker has to travel **under their own steering** to have
  // "walked", and then to have felt the lead. In world units: PLAYER.SPEED is
  // 220/s, so these are a second and a half, then three more. Long enough that
  // each is a stretch of walking rather than a twitch — the first cut of these
  // was a third of this and the opening cards blazed past unread.
  WALK_PX: 300,
  LEAD_PX: 900,
  // How long a **briefing** stays up: a card that explains something and has
  // nothing to do, so nothing but the clock can finish it. A sentence about
  // what a critter does to a dog is about this much reading at a walking pace.
  BRIEF_MS: 5000,
  // The floor on how long any card stays up, so a lesson the player finishes
  // instantly is still readable. Lifted only when the walk has genuinely left
  // the lesson behind — being told to walk about while carrying the bag is
  // worse than missing the card.
  MIN_SHOW_MS: 1600,
  // How soon the dog should need the grass once the lesson about it comes up.
  // The urge is held back through the earlier lessons, so simply releasing it
  // would mean the usual DOG_STATE.POOP_MIN_MS of "watch the dog" with nothing
  // to watch; this brings it on a beat after the card appears.
  URGE_WITHIN_MS: 2500,
  // How close to a carriageway counts as "about to cross it" — what cues the
  // one car of the guided walk. Close to a second of walking, which is roughly
  // what a car released off the map edge needs to arrive as they reach the kerb.
  CROSSING_NOTICE_PX: 190,
  // How long an aside about a car or a critter stays on the card. Long enough
  // to read mid-walk, short enough that it is gone before the next one.
  TIP_MS: 5200,
  // How much frenzy counts as "the dog has spotted something". Just off zero:
  // the meter only moves when a critter is pulling, so anything above nothing
  // is the event the aside is about.
  CRITTER_TIP_AT: 0.02,
} as const;

/** Objective tuning: bagging it up, poop-bag disposal, and the home stretch. */
export const OBJECTIVE = {
  /**
   * How long the walker spends bagging it up, once the dog has finished.
   *
   * There used to be a whole objective phase here: the player stood over the
   * mess and held a control for `CLEANUP_HOLD_MS` (three seconds) to pick it
   * up, with its own meter, its own on-screen button and its own range test.
   * None of it was a decision — the dog is an anchor while it is going, so the
   * walker was clamped inside the range wherever they stood, and the hold was
   * three more seconds of standing still bolted onto the three they had just
   * spent watching the squat. So the two are one beat now, and this is the
   * length of the second half of it: long enough to read as the walker bending
   * down and picking it up, short enough not to be a wait.
   *
   * The dog stays rooted for it (`Dog.isBusy`), which is what stops the lead
   * dragging it off the mess mid-scoop.
   */
  SCOOP_MS: 900,
  // Trash bin — a persistent world object. Bag is dropped automatically within range.
  BIN_RADIUS: 60,
  BIN: { X: 140, Y: 120, SIZE: 34, COLOR: PROPS.BIN },
  /** Pressed seams round the bin's body. See render/world.ts → buildBin. */
  BIN_RIBS: 8,
  BIN_FOOT_COLOR: MASONRY.PLINTH,
  BIN_LID_COLOR: PROPS.BIN_LID,
  BIN_MOUTH_COLOR: PROPS.BIN_MOUTH,
  BAG_COLOR: PROPS.BAG, // little bag drawn on the player while carrying
  /**
   * The walker's own plot: the house at the back of it and the front garden in
   * front, which is where the walk starts and where it has to end.
   *
   * The whole rect is the win zone. Only the **glow** over it waits for the bag
   * to be binned — the house itself stands there from the first frame, because
   * the player is looking at it as they set off (see systems/home.ts).
   */
  HOME: { X: 820, Y: 540, WIDTH: 120, HEIGHT: 120, COLOR: PROPS.HOME },
  /**
   * The cottage standing on the home plot — the thing the player walks away
   * from and then back to, so it gets the same treatment as the town
   * (see render/buildings.ts → buildCottage).
   *
   * Its own colours rather than a town building's: home should be the warmest,
   * friendliest house on the map and read as *yours* from across the street.
   * BAND is its painted skirt, much shorter than a shopfront — this is a
   * cottage, not a shop.
   *
   * WIDTH/DEPTH are its footprint, and DEPTH > WIDTH is load-bearing: the ridge
   * runs along the longer side, so a cottage deeper than it is wide turns a
   * gable end to the approach instead of a roof slope (see render/buildings.ts
   * → "Every ridge runs front-to-back"). Both, plus DOORSTEP — the gap between
   * the front wall and the doormat the walk begins on — have to fit inside
   * HOME above with room for the walker; `systems/home.test.ts` holds them to
   * it.
   */
  COTTAGE: {
    WALL: WALLS.RENDER_CREAM,
    ROOF: ROOFS.TILE,
    PAINT: PAINTS.RACING_GREEN,
    BAND: 14,
    WIDTH: 56,
    DEPTH: 60,
    DOORSTEP: 28,
  },
} as const;

/**
 * Ground — the walkable surface. Everything is paved except the grass "park"
 * patches; both are walkable. Buildings (below) are the only obstacles, and roads
 * cut across as crossing zones. Rects are {x, y, w, h} with x/y the top-left.
 */
export const GROUND = {
  // Base fill for the block interiors — deliberately duller than the footways so
  // the pavement alongside each road reads as a distinct surface.
  BASE_COLOR: GROUND_COLORS.BLOCK_INTERIOR,
  PARK_COLOR: GROUND_COLORS.GRASS,
  PARK_KERB_COLOR: GROUND_COLORS.GRASS_KERB, // low surround the grass sits on
  PARK_KERB_HEIGHT: 2,
  /**
   * Mown stripes on the grass: the world size of one tile, and its relief.
   *
   * The cell is big on purpose — the bands a mower leaves are a *large* feature,
   * and shrinking them turns a lawn into corduroy. Sized so a park a couple of
   * hundred units across gets a handful of stripes rather than dozens.
   */
  GRASS_CELL: 120,
  GRASS_RELIEF: 0.3,
  /**
   * The same aggregate speckle the road uses, for the yards inside the blocks —
   * the second-largest surface in the game, and one that sits directly against
   * textured paving on one side and coursed brick on the other.
   *
   * A larger cell and less relief than the road: this is hardstanding seen from
   * further back, and it must stay quiet enough to read as background.
   */
  YARD_CELL: 130,
  YARD_RELIEF: 0.14,
  // The apron drawn well past the level so the map has no hard edge against the
  // sky. It is out of bounds, so `OUT_OF_BOUNDS` takes the colour back out of it
  // — the hue here is what the fog sees, not what the player does.
  SURROUNDS_COLOR: GROUND_COLORS.SURROUNDS,
  OUT_OF_BOUNDS_EXTENT: 180,
  /**
   * The town's greens.
   *
   * There used to be a second one — 160 × 150, at (472, 470), commented "green
   * in front of home", and that comment was the whole problem. The dog will
   * only go on grass, so a green a hundred units from the front gate means the
   * first half of the walk is over before the player has left their own street:
   * out of the door, onto the grass, done. Finding somewhere for the dog to go
   * is meant to be half the walk, and `validate.ts` → `grass-on-the-doorstep`
   * now says so for every level, this one included.
   *
   * There is nowhere else in a 960 × 640 town to put it — the best spot left
   * clears the home plot by 132, and the rule wants 220 — so it is gone rather
   * than moved, and the walk goes to the bottom-left park like everybody
   * else's. That green is the bigger and better of the two anyway.
   */
  PARKS: [{ x: 24, y: 430, w: 176, h: 182 }],
} as const;

/**
 * How the town beyond `LevelSpec.bounds` is dressed down.
 *
 * The walker is clamped to `bounds` and the town carries on past it, so the ring
 * has to read as *you can't go there* — while still reading as a town, because
 * the whole reason the surround exists is that a level which stops dead at its
 * own edge looks like a diorama.
 *
 * One filter over the non-playable area does it, in
 * `render/clay.ts` → `greyOutsideBounds`. **Nothing is built in a different
 * colour**; a shader takes the colour out per fragment, which is the only way
 * the boundary can land mid-cottage where it has to.
 *
 * The numbers are deliberately gentle. Painting the ring dark slate came first
 * and read as night falling over next door — the eye takes a brightness step as
 * weather or shadow, and only a *saturation* step as "not part of this". So the
 * tone is left almost alone and the colour comes out instead.
 */
export const OUT_OF_BOUNDS = {
  /** How much of the colour to take out. 1 would be full greyscale. */
  DESATURATION: 0.72,
  /** A whisker of shade, to keep the boundary visible on already-grey paving. */
  DIM: 0.05,
  /**
   * World units the transition is smeared over.
   *
   * The boundary runs along the ground axis-aligned to the screen, where a hard
   * step is perfectly clean — but it also crosses roofs, canopies and gable
   * slopes at every other angle, and an un-feathered step crawls along those as
   * the camera moves. Small enough to still read as a line.
   */
  FADE: 6,
} as const;

/**
 * The gardens — everything inside a block that isn't a house.
 *
 * Until this existed, all of it was the aggregate above, which from overhead
 * read as a car park with cottages parked on it: a side passage between two
 * terraces, the strip behind them and an undeveloped corner were all the same
 * flat grey, so a block had no interior at all.
 *
 * It's now rough ground with hedges and weeds on it. Where each piece goes is
 * worked out by the pure `systems/yards.ts`; this is how big and how dense.
 *
 * **None of it is solid.** The game's rule is that trunks collide and foliage
 * does not — you walk under branches — and a bush and a clump of weeds are all
 * foliage. That's what lets the planting go wherever it looks right, including
 * across a narrow gap, without pinching a route or trapping the dog. Wading
 * through long grass is also what "overgrown" should look like.
 */
export const YARDS = {
  /**
   * Rough ground: the world size of one tile of the scrub pattern, and its
   * relief.
   *
   * A smaller cell than the mown park's, and more relief. Both say the same
   * thing — this surface is closer to the eye than a park (you walk down a side
   * passage, you look across a green) and it is meant to look shaggy rather
   * than cut.
   */
  CELL: 96,
  RELIEF: 0.42,
  COLOR: GROUND_COLORS.SCRUB,
  SOIL_COLOR: GROUND_COLORS.SCRUB_SOIL, // bare patches worn through it
  /** Rough ground sits a hair above the base fill so the two can't z-fight. */
  LIFT: 0.35,
  /**
   * The narrowest strip of free ground worth treating.
   *
   * Below this it's the gap between two cottages that were meant to touch, and
   * planting a hedge in one puts a bush inside a wall.
   */
  MIN_PATCH: 14,
  /**
   * Hardstanding left along every block edge, between the frontages and the
   * footway.
   *
   * Without it the rough ground runs to the kerb and every street is fringed
   * with weeds, which reads as abandoned rather than lived-in. With it the
   * cottages keep a paved apron and the scruff sits behind them — and a side
   * passage gets the right gradient for free: paved at the street, gone to seed
   * at the far end.
   */
  STREET_MARGIN: 16,

  // ── Bushes ────────────────────────────────────────────────────────────────
  /**
   * Grid pitch for bushes, and how far they stay off a patch's edges.
   *
   * The inset is the important one: a patch's edges are building walls, and a
   * bush centred on the boundary is half inside a cottage.
   */
  BUSH_SPACING: 54,
  BUSH_INSET: 13,
  /**
   * How far a bush stays off the bin, the home zone and the player's start.
   *
   * These are the three places the player is sent to or begins from, and a
   * hedge grown over one is a bug rather than scenery — the bin especially,
   * since finding it is an objective and it is already a dark green object.
   */
  OBJECTIVE_CLEARANCE: 46,
  /** Balls per bush, and the envelope they fill at scale 1. */
  BUSH_BLOBS: 4,
  BUSH_RADIUS: 11,
  /**
   * How much lighter the crown of a bush is than its underskirt, either side of
   * its own leaf colour.
   *
   * The same rule as the tree canopies and every character in the game: from
   * straight above, a single-colour rounded mass is a featureless lump, and a
   * bush is nothing but that. Pushed a little harder than the trees' 0.17 —
   * a bush is smaller, so it has fewer pixels to make the point in.
   */
  BUSH_SHADE_SPREAD: 0.2,
  /** Squashed rather than spherical: a bush spreads wider than it stands. */
  BUSH_SQUASH: 0.72,

  // ── Weeds ─────────────────────────────────────────────────────────────────
  /**
   * Weed tufts along the inside edges of each patch, and how far in they
   * straggle.
   *
   * Edges rather than an even scatter, and this is the whole trick. Spread
   * evenly, greenery reads as *planting* — deliberate, tended. What reads as
   * neglect is growth banked up where a wall meets the floor while the middle
   * stays worn, which is also where it really grows.
   */
  WEED_SPACING: 11,
  WEED_BAND: 16,
  /**
   * Blades per tuft, and their height and lean at scale 1.
   *
   * Tall for a weed — a third of the walker's height. Anything shorter is
   * indistinguishable from the tussocks already drawn in the ground texture,
   * and the point of modelling these at all is that they stand *off* the
   * surface and catch their own light.
   */
  WEED_BLADES: 6,
  WEED_HEIGHT: 10,
  WEED_THICKNESS: 1.3,
  WEED_LEAN: 0.42, // radians the outer blades splay
} as const;

/**
 * The furniture on the greens — a fountain, a bench, a phone kiosk, a pillar
 * box. How many, how big a green has to be to carry one, and how far it stands
 * from the kerb; *which* piece goes where is worked out by the pure
 * `systems/parkFurniture.ts`, and the models are in `render/models.ts`.
 *
 * Sparse on purpose. These are landmarks — something to walk the dog past and
 * to say where you are on the map — and a bench on every green is scenery
 * again rather than a landmark.
 */
export const PARK_FURNITURE = {
  /**
   * The smallest green worth furnishing, measured on its *shorter* side.
   *
   * Two insets plus the widest piece: a green that can't take the fountain in
   * the middle with grass showing all round it reads as a paved yard with an
   * ornament on it, and the dog still has to be able to get properly onto the
   * grass beside whatever is standing there.
   */
  MIN_PARK: 90,
  /** A green needs this much room on both sides before it gets the fountain. */
  FOUNTAIN_PARK: 150,
  /** How far in from the kerb the path runs, and so where an edge piece stands. */
  EDGE_INSET: 26,
  /**
   * How wide that path is.
   *
   * A footway is `PAVEMENT.WIDTH` because two people and a dog pass on it; this
   * one is scenery the walker crosses rather than follows, and drawn much wider
   * it stops being a path round a lawn and starts being a paved yard with grass
   * in the corners.
   */
  PATH_WIDTH: 14,
  /**
   * How wide a green has to be before it gets two benches instead of one.
   *
   * A single bench in the middle of a long south edge reads as the one thing
   * they could afford; a pair at the thirds reads as a park.
   */
  TWO_BENCH_PARK: 260,
  /** How far apart the blooms in a bed sit, and how big one bed is. */
  BED_RADIUS: 13,
  BED_BLOOMS: 7,
} as const;

/**
 * Footways (pavements) running alongside every road — where you're meant to walk
 * the dog, with the carriageway as the thing you only cross.
 *
 * WIDTH is the clear walkable band on each side. It's set at 3× the player's
 * radius so there's genuine room to walk with the dog rather than a token strip,
 * and the BUILDINGS below are laid out to leave it unobstructed:
 *
 *   road at `pos`  →  carriageway  pos ± ROADS.WIDTH/2
 *                  →  footway      out to pos ± (ROADS.WIDTH/2 + PAVEMENT.WIDTH)
 *
 * Footways stop at each crossroads rather than running over live tarmac — see
 * systems/pavement.ts for that span arithmetic.
 */
export const PAVEMENT = {
  WIDTH: 48, // clear walkable band; player diameter is 32
  KERB_HEIGHT: 4, // how far the footway stands above the tarmac
  COLOR: GROUND_COLORS.PAVING, // paving slabs
  KERB_COLOR: GROUND_COLORS.KERB, // the kerb face dropping to the road
  JOINT_COLOR: GROUND_COLORS.PAVING_JOINT, // joints, now drawn by the paving pattern
  /**
   * Relief on the paving bond.
   *
   * Higher than the tarmac's: a paving joint is a real recess and the footway is
   * the surface the player spends the whole game walking along, so it is worth
   * the light catching each slab edge. The slab size itself is PAVING_CELL in
   * render/masonry.ts, sized off WIDTH so a whole number fits across.
   */
  PAVING_RELIEF: 0.45,
} as const;

/**
 * Cobbled pedestrian lanes — the alleys cutting through a block.
 *
 * A lane is a road with no carriageway: walkable ground the whole width, no
 * kerb, no centre line, and **never any traffic**. That last one is enforced
 * three times over (`isStreet` gates the traffic planner, and `validate.ts`
 * re-checks it from scratch) because a car on a footpath is not a difficulty
 * spike, it is a bug that can end a run with nothing the player could have done.
 *
 * Lanes earn their place twice. They break a block into pieces that no grid
 * would produce, and they give the *inside* of a block a frontage — without one,
 * a block only builds along its street edges and its middle is dead ground.
 *
 * **A lane is exactly a pavement wide**, and that is a definition rather than a
 * tuning. It means a lane is precisely as walkable as the surface the rest of
 * the game is played on — already checked against the walker's diameter and the
 * dog beside them — and it means no piece of geometry needs a second width
 * threaded through it. Every derivation that already knows `PAVEMENT.WIDTH`
 * can lay a lane without being told anything new, which is the difference
 * between one place knowing about lanes and a dozen places having to.
 */
export const LANES = {
  WIDTH: PAVEMENT.WIDTH,
  COLOR: GROUND_COLORS.COBBLE,
  JOINT_COLOR: GROUND_COLORS.COBBLE_JOINT,
  /**
   * A lane sits at the same height as a footway, so the two are continuous
   * where a lane comes out onto a street. It gets no kerb of its own — there is
   * no carriageway beside it to be kerbed off from.
   */
  HEIGHT: PAVEMENT.KERB_HEIGHT,
  /**
   * Relief on the setts, above the paving's. A cobble is a rounder, deeper
   * joint than a paving slab, and it is the main thing telling the two
   * surfaces apart from a near-overhead camera where colour alone is weak.
   */
  RELIEF: 0.62,
} as const;

/**
 * Buildings — solid blocks you cannot walk through (static collision). Placed in
 * the crossroads "blocks", clear of the bin, home, player start, and leaving
 * pavement lanes so every objective stays reachable. {x, y, w, h} = top-left.
 *
 * FOOTWAY CLEARANCE: every building is kept off the pavement, so the walkable
 * band beside each road is the full PAVEMENT.WIDTH rather than whatever's left
 * over. With the roads at y=232 and x=384 that means buildings must stay within:
 *
 *   north of the h-road   y + h ≤ 152        south of it   y ≥ 312
 *   west of the v-road    x + w ≤ 304        east of it    x ≥ 464
 *
 * Move a road or change PAVEMENT.WIDTH and these bounds move with it — check the
 * buildings still clear the footway, or the pavement gets pinched to nothing.
 */
export const BUILDINGS = {
  ROOF_COLOR: ROOFS.SLATE, // fallback when a building doesn't name one
  WINDOW_COLOR: GLASS.LIT,
  WINDOW_DARK_COLOR: GLASS.UNLIT,
  CHIMNEY_COLOR: ROOFS.CHIMNEY,
  CHIMNEY_POT_COLOR: ROOFS.CHIMNEY_POT,
  /** The corbelled cap on a chimney stack: how far it oversails, and how deep. */
  /**
   * How far a stack stands above the ridge it sits on.
   *
   * Generous, because from this camera a chimney is seen mostly from *above*:
   * too short and all you get is the cap, a brown square lying on the roof. The
   * height is what turns it back into a stack with sides to catch the light.
   */
  CHIMNEY_RISE: 18,
  CHIMNEY_CAP_PROUD: 1.4,
  CHIMNEY_CAP_HEIGHT: 4,
  /** Stacks wider than this get a second pot. */
  CHIMNEY_TWIN_AT: 10,
  PLINTH_COLOR: MASONRY.PLINTH,
  /**
   * Roof pitch as a fraction of the building's smaller footprint dimension.
   * The resulting slope angle is `atan(2 × ROOF_PITCH)`.
   *
   * WATCH THE CAMERA ANGLE, but watch *which way the ridge runs* first.
   * `CAMERA.TILT_DEG` of 58° means the view direction is 32° off vertical, so a
   * slope near 32° that faces the camera points its face straight down the lens
   * and renders as one enormous featureless quad. That's what made 0.42 (a 40°
   * slope) fail, and why the pitch then spent a long time at 0.22 — flattened
   * to duck *under* the bad angle, which is also what made the houses read as
   * blocks with lids rather than as cottages.
   *
   * The cottages solve it a better way: every ridge runs **front-to-back**, so
   * no slope ever faces the camera at all (see systems/terrace.ts). Both slopes
   * are seen edge-on across the view, where a steep pitch is *more* legible
   * rather than less. That frees the pitch to go where the look wants it —
   * 0.62 is a 51° slope, so the roof carries about as much height as the walls,
   * which is the single biggest thing separating a cottage from an office block.
   *
   * If you ever put a ridge back across the view — a hip, or a gable turned
   * side-on to the street — that slope faces the lens and this constraint bites
   * again.
   */
  ROOF_PITCH: 0.62,
  /**
   * Radius of the rolled clay ridge capping each gable. 0 disables it.
   *
   * Kept slim. The ridge now runs front-to-back, so it reads as a line straight
   * down the middle of the roof rather than across it, and anything fat there
   * stops looking like a rolled tile and starts looking like a pipe laid on the
   * house.
   */
  RIDGE_RADIUS: 2.4,

  // ── The roofline ────────────────────────────────────────────────────────
  // From this camera the roof is the largest thing on any building by a wide
  // margin, so it's where detail pays best. Everything here is edge trim: the
  // slopes themselves stay a plain tiled surface (see the note on surface
  // detail below), and what stops them reading as slabs of cheese is having a
  // proper edge where they stop.
  /**
   * A fascia board between the wall top and the tiles, running right round the
   * building at the roof's own overhanging footprint.
   *
   * This is the single biggest change to a roof's silhouette: without it the
   * tiles meet the wall in a knife edge and the roof looks like a lid balanced
   * on the box. Note the walls still stop at `tall` — the band and everything
   * above it are extra height, so a building is `tall + EAVES_HEIGHT + pitch`
   * from pavement to ridge.
   */
  EAVES_HEIGHT: 6,
  /** Depth of the moulded lip along the bottom of that board. */
  EAVES_LIP: 1.6,
  /**
   * Half-round tiles capping a ridge or a hip's corner.
   *
   * SPACING is deliberately half `MASONRY_WIDTH`, so the bumps along a ridge
   * are the same rhythm as the tiles below them — at the full cell they read as
   * a row of eggs, and much finer they disappear into the ridge roll entirely.
   */
  RIDGE_TILE_SPACING: 11,
  RIDGE_TILE_RADIUS: 3,
  /** The rolled tiles down each sloping corner of a hipped roof. */
  HIP_ROLL_RADIUS: 3,
  /** Barge boards down the two sloping edges of a gable end. */
  BARGE_WIDTH: 4, // out from the gable wall
  BARGE_HEIGHT: 7, // across the board, i.e. how deep it hangs
  /** Thickness of the triangular gable wall closing the end of a ridged roof. */
  GABLE_THICKNESS: 6,
  /** The round attic window in the middle of a gable end. */
  ROUNDEL_RADIUS: 7,
  ROUNDEL_FRAME: 2.2,

  // ── Rooflights ──────────────────────────────────────────────────────────
  // Glazing laid flat *into* a slope, to break up the roof — which is the one
  // really big blank surface left in the town, and the one this camera spends
  // most of its time looking straight at.
  //
  // WHY NOT DORMERS. They were built first and thrown away, and the reason is
  // ROOF_PITCH above. A dormer has to fit between the slope it stands on and
  // the ridge: its seat is partway up, then its wall, then its own little
  // roof. At a 0.22 pitch the rise is only 17–30 units on these footprints, so
  // a dormer with a window in it comes out taller than the roof it's sitting
  // on and breaks the ridge line. A rooflight has no height at all, so the
  // shallow pitch costs it nothing — and lying flat on the slope is exactly
  // where a 58° camera is looking.
  //
  // `UP` is the fraction from eave to ridge, so a value near 0.5 keeps them
  // clear of both the fascia and the ridge tiles.
  ROOFLIGHT_W: 17,
  ROOFLIGHT_H: 12,
  ROOFLIGHT_UP: 0.5,
  /** Kerb around the glazing: how far it stands proud, and past the glass. */
  ROOFLIGHT_KERB: 2,
  ROOFLIGHT_PROUD: 1.4,
  /** Glass reflecting the sky rather than lit from inside — see buildings.ts. */
  ROOFLIGHT_COLOR: ATMOSPHERE.SKY,
  /** Slope length along the eave past which a slope gets two rather than one. */
  ROOFLIGHT_PAIR_AT: 110,

  // ── Surface detail ──────────────────────────────────────────────────────
  // Roofs and brick walls are the two biggest flat areas in the game, and a
  // big flat area of clay reads as a blank slab. Both get a running-bond
  // pattern — as a **texture on the shell of the building**, with the windows,
  // doors and drainpipes as meshes standing on top of it.
  //
  // It was built the other way round first: one little box per tile. Don't go
  // back. A box only shows up if it stands *off* the surface, so the roofs read
  // as gritted rather than tiled, and it cost ~150k triangles to say what a
  // 256-pixel canvas says better. See render/masonry.ts.
  //
  // The size below is one cell of the pattern, in world units, and it holds at
  // that size on every building regardless of its dimensions — that's what the
  // per-face repeat counts in systems/coursing.ts are for. It's purely a look
  // decision: nothing here costs triangles.
  //
  // **One cell for both**, so a roof tile and a brick are the same size. The
  // town is hand-pressed clay, not a scale model, and the two patterns reading
  // at a single scale is what makes it look like one material worked two ways.
  //
  // Sizing it is a straight fight with the camera distance rather than with
  // realism. Life-size is far too fine — 1 unit ≈ 10cm, so a real brick is
  // about 2 units and vanishes into grain from up here. Tiles were tried at
  // 13×8 (crazy paving), 7×5 (gone), and 10×6 (fine, but finer than the
  // brickwork beside them). 22×9 is about three courses across the narrowest
  // terrace, which is as chunky as it can go while still repeating.
  /** Length of one brick or roof tile, along its course. */
  MASONRY_WIDTH: 22,
  /** Height of one course — up the wall, or up the slope on a roof. */
  MASONRY_COURSE: 9,
  /** Relief on the pattern's bump map. Enough to catch a highlight along the
   *  top edge of each unit; more than this and the joints read as cracks.
   *  Roofs take more: they face the light nearly flat-on, where a wall is
   *  steeply raked and its relief exaggerated by the grazing angle. */
  TILE_RELIEF: 0.7,
  BRICK_RELIEF: 0.45,

  // ── Front patios ────────────────────────────────────────────────────────
  // A paved forecourt between the front door and the pavement, on the
  // buildings flagged `patio`. Those buildings are pulled back from the
  // footway by PATIO_DEPTH in the list below to make room — the patio fills
  // the gap, so it sits *between* the house and the street rather than on it.
  //
  // Purely decorative: the slab and its kerb are the same height as the
  // footway kerb, which the player already walks over freely, so nothing here
  // has collision. Adding a solid wall this close to the pavement would pinch
  // the walkable route.
  PATIO_DEPTH: 20,
  PATIO_INSET: 4, // margin from the building's side walls
  PATIO_HEIGHT: 2.5,
  PATIO_EDGE_WIDTH: 2.5,
  PATIO_PATH_WIDTH: 16,
  PATIO_COLOR: MASONRY.PATIO_PAVING,
  PATIO_EDGE_COLOR: MASONRY.PATIO_EDGE,
  PATIO_PATH_COLOR: MASONRY.PATIO_PATH,
  /** Overhang past the walls, so roofs cast a proper eaves shadow line. */
  ROOF_OVERHANG: 5,
  // ── One plot, several cottages ──────────────────────────────────────────
  // A plot in LIST below is a collision rect, not a house. Drawing one box per
  // plot is what made the town read as eight office blocks — eight identical
  // extrusions, each as wide as a block, each with a single front door.
  //
  // `systems/terrace.ts` cuts each plot into individual cottages across its
  // frontage instead, each with its own height, paint, door and roof. The bays
  // tile the plot exactly, so the town is solid in precisely the same places:
  // this changes what's drawn, never where you can walk.
  //
  // MIN_WIDTH is a hard floor and the count is floored, not rounded — a cottage
  // narrower than this can't fit a door and its architrave with wall left over.
  // How much *deeper* than wide it then has to come out is MIN_RATIO below,
  // which is what turns its ridge front-to-back and its gable end to the
  // street. `terrace.test.ts` pins both.
  BAY_MIN_WIDTH: 46,
  BAY_MAX: 3,
  /**
   * How many times deeper than wide a cottage has to come out.
   *
   * The ridge rule used to be the bare inequality "deeper than it is wide",
   * which `gableSlope` needs to run the ridge north–south and nothing more. A
   * bare inequality is satisfied by a cottage 78 wide and 79 deep, and that is
   * what the town filled up with: 42% of cottages came out within a fifth of
   * square, and a square cottage has a square roof. The ridge is still
   * technically north–south, but there is no long axis for the eye to read it
   * along, so the roof lands as a lid on a box and which way it runs looks
   * arbitrary — half of them look turned the wrong way round.
   *
   * So the rule is a *proportion*, not an inequality. This is the number that
   * makes a cottage read as a cottage: narrow to the street, long back into the
   * plot, with a ridge that plainly runs the way it should. Raising it makes
   * the town handsomer and its plots hungrier — every cross-street plot needs
   * this much frontage per bay, so too high and small blocks stop fitting a
   * terrace at all. `terrace.test.ts` and `town.test.ts` pin both ends.
   */
  BAY_MIN_RATIO: 1.35,
  /** Fraction the widths vary by, so a row never reads as one shape repeated. */
  BAY_JITTER: 0.18,
  /** Height difference between neighbouring cottages, in a stepped roofline. */
  BAY_STEP: 7,

  // ── Cottage proportions ─────────────────────────────────────────────────
  // Two low storeys under a big roof, rather than the three or four even ones
  // the old blocks had. Sized against the 46-tall walker: a ground floor a
  // little under twice his height, and an upper floor shorter than it.
  STOREY_GROUND: 34,
  STOREY_UPPER: 28,
  /**
   * How far the upper storey oversails the ground floor.
   *
   * A jetty is the other half of what stops a house being an extrusion: the
   * wall gets a horizontal break with a real shadow under it, and the
   * silhouette stops being a single unbroken vertical. Kept small — it hangs
   * past the collision rect, and while it has no collision of its own, a big
   * one would be drawn out over the pavement.
   */
  JETTY: 2.5,
  /**
   * Ground-floor band painted in the cottage's accent colour.
   * It must be TALLER than DOOR_HEIGHT — the door sits inside this band, and if
   * the band is shorter the door pokes up into the first row of windows.
   */
  SHOPFRONT_HEIGHT: 34,
  /**
   * A moulded string course capping the shopfront band, and a stone plinth at
   * the building's foot.
   *
   * Both are thin horizontal bands standing proud of the wall, and between them
   * they do what the painted band alone couldn't: give the ground floor a top
   * and a bottom. The plinth stays *shorter* than the doorstep so the step
   * still reads as a step rather than merging into the base.
   */
  STRING_COURSE_HEIGHT: 2.6,
  STRING_COURSE_PROUD: 2,
  PLINTH_HEIGHT: 3.5,
  PLINTH_PROUD: 2.5,
  // Front door, on whichever face looks onto a street (see systems/level.ts →
  // frontFacing). Sized against the 46-tall walker so it reads as a real door.
  //
  // The head is a semicircle of DOOR_WIDTH/2, so the whole opening is
  // DOOR_HEIGHT tall including the arch — the arch eats into the door rather
  // than standing on top of it, which is what keeps it inside SHOPFRONT_HEIGHT.
  DOOR_WIDTH: 15,
  DOOR_HEIGHT: 28,
  /** Vertical boards pressed into the door face, in a shade of its own timber. */
  DOOR_PLANKS: 3,
  DOOR_COLOR: DOORS.TIMBER,
  DOOR_FRAME_COLOR: DOORS.FRAME,
  DOOR_STEP_COLOR: DOORS.STEP,
  DOOR_KNOB_COLOR: DOORS.KNOB,
  /** Carriage lamps flanking the door — a dark bracket with a lit globe. */
  DOOR_LAMP_COLOR: DOORS.LAMP,
  DOOR_LAMP_RADIUS: 2.2,

  /**
   * Facade layout. Windows go on **all four** faces — a blank side wall is very
   * obvious from this camera, since you routinely see two faces of a building
   * at once.
   *
   * The first upper row starts above SHOPFRONT_HEIGHT so it can't collide with
   * the door, and the door's slot on the ground floor is skipped rather than
   * being filled with a shop window on top of it.
   */
  STOREY_HEIGHT: 26, // vertical gap between window rows
  // Horizontal gap between window columns — the main dial for how busy a facade
  // looks. Note it sets the count as well as the gap: columns are
  // floor((faceWidth - 2*FACE_MARGIN) / spacing), so widening this both spreads
  // the windows out and thins them.
  //   28 → up to 4 per face, reads as an apartment block
  //   36 → 1–3 per face
  //   44 → 1–2 per face, well separated (current)
  WINDOW_SPACING: 44,
  WINDOW_W: 10, // wide enough to read as a sash, not a slot
  WINDOW_H: 13,
  // A sill under each window, and nothing above it. A sill *and* a lintel on
  // every window across four faces stacks up into a ladder of horizontal bars.
  WINDOW_SILL_COLOR: DOORS.SILL,
  SHOP_WINDOW_W: 15, // wider glazing on the ground floor
  SHOP_WINDOW_H: 14,

  // ── What's around a pane of glass ───────────────────────────────────────
  // A bare rectangle of glass on a wall reads as a sticker. Three things fix
  // that, and all three are instanced (see buildFacades) because they're the
  // most numerous meshes in the town by an order of magnitude:
  //
  //   the SURROUND  a stone frame standing proud, with the glass set into it
  //   the BARS      one glazing bar each way, so a pane looks divided
  //   the SHUTTERS  a leaf either side, in the building's own paint
  //
  // Note the surround is what the glass now sits *in*: WINDOW_PROUD is
  // measured past it, not past the wall.
  WINDOW_SURROUND_PAD: 3, // how far the frame stands out past the glass
  WINDOW_SURROUND_DEPTH: 1.6,
  WINDOW_PROUD: 0.5, // glass, past the face of its surround
  GLAZING_BAR: 1.2,
  /**
   * Shutter leaves, on upper-storey windows only.
   *
   * The ground floor is the building's painted band already, and a shutter on a
   * shop window puts the same colour twice in the same place. Keep the leaf
   * narrower than half the pane or a closed-looking pair starts reading as a
   * boarded-up window from overhead.
   */
  SHUTTER_W: 4.5,
  SHUTTER_GAP: 0.8, // clearance from the surround
  /**
   * Window boxes, on the FIRST-FLOOR windows of the FRONT face only.
   *
   * Every window in town would be a flowerbed; one row on the face you actually
   * approach the house from is the difference between a town that's been lived
   * in and a facade with a rash.
   */
  BOX_W: 15,
  BOX_H: 4.5,
  BOX_D: 4.5,
  BOX_BLOOM_R: 1.9,
  BOX_COLOR: FOLIAGE.PLANTER,
  BOX_LEAF_COLOR: FOLIAGE.BOX_LEAF,
  BOX_BLOOMS: FOLIAGE.BLOOMS,
  /** Margin kept clear at each end of a face, so windows don't hit the corner. */
  FACE_MARGIN: 18,
  /**
   * The same idea for the **ground floor of the street face**, which is laid out
   * around the front door rather than on the column grid (systems/terrace.ts →
   * frontPlan). Much smaller than FACE_MARGIN: that one is sized to keep windows
   * off the corner of a 126-wide block, and on a 52-wide cottage it would eat
   * the entire frontage and leave the door standing alone on blank paint.
   */
  FRONT_MARGIN: 3,
  /**
   * Clear wall between the door and the window beside it.
   *
   * Both of these are tight, and deliberately: the narrowest cottage in town is
   * BAY_MIN_WIDTH across, and its architrave plus one window plus these gaps
   * only just fit. `terrace.test.ts` asserts every cottage still gets a
   * ground-floor window, so if BAY_MIN_WIDTH drops or the door grows, that test
   * is the thing that tells you.
   */
  FRONT_GAP: 2,
  WINDOW_LIT_CHANCE: 0.45, // fraction of windows that glow
  // NOTE: `w`/`h` are the GROUND footprint (matching systems/level.ts Rect, which
  // the collision + spawn-avoidance code uses). Each entry is a **plot** — a
  // block of the town — and is cut into one to three individual cottages across
  // its frontage by systems/terrace.ts. The rect is what you collide with; the
  // cottages are what you see.
  //
  // `tall` is the plot's base wall height, which the cottages step either side
  // of by BAY_STEP so no two neighbours share an eaves line. Keep it high enough
  // that the lowest cottage still clears a row of upper windows (the facade pass
  // needs `tall` ≥ SHOPFRONT_HEIGHT + STOREY_HEIGHT/2 + WINDOW_H/2 + 6), and
  // low enough to stay townhouse-scale: two storeys under a big roof, against a
  // 46-tall walker. Tower blocks would read as a city and fight the whole look.
  //
  // `tones` is the kit each cottage on the plot is painted from, taken in order
  // and wrapped if there are more cottages than tones — so a pair or a row of
  // three never comes out as one colour. Per tone: `color` is the wall,
  // `roofColor` the tiles, `paint` the door and the painted ground floor (the
  // one saturated note per cottage), and `material` decides whether the walls
  // get brick coursing or stay smooth render, which is the main thing telling
  // two similarly coloured cottages apart.
  //
  // `patio` gives the plot a paved forecourt across its whole front — those
  // entries are already pulled back from the footway by PATIO_DEPTH to make
  // room, so changing one back means giving its depth back too.
  //
  // TWO RULES FOR PATIOS, both learned the hard way:
  //
  // 1. ONLY ON A SOUTH-FACING FRONT. A building is drawn well up the screen
  //    from its own footprint at this camera tilt (see AGENTS.md), and its roof
  //    most of all. A patio on the *north* side of a house therefore sits
  //    entirely underneath that projection and is completely invisible. Only
  //    the houses in the northern block face the camera, so only they can have
  //    one.
  //
  // 2. WATCH THE SETBACK. Both the front doors and the patio go on whichever
  //    face `frontFacing()` picks, and that is decided by which face is
  //    *nearest a road* — so pulling a plot back can hand the title to a
  //    different face and swing the whole terrace round the side. Only give a
  //    patio to a plot whose front wins by more than PATIO_DEPTH. The 236,16
  //    and 236,312 pair sit exactly equidistant from two roads and flipped to
  //    facing east when they were first tried with one; pavementClearance.test.ts
  //    caught it.
  LIST: [
    // Top-left block (bin is at 140,120 — kept clear). Narrow frontage: one
    // cottage, the tallest thing on the street.
    { x: 236, y: 16, w: 68, h: 136, tall: 74, patio: false,
      tones: [
        { color: WALLS.BRICK, roofColor: ROOFS.SLATE, paint: PAINTS.RACING_GREEN,
          material: 'brick' },
      ] },
    // Top-right block: a pair.
    { x: 464, y: 16, w: 126, h: 136, tall: 70, patio: false,
      tones: [
        { color: WALLS.RENDER_CREAM, roofColor: ROOFS.TILE, paint: PAINTS.POSTBOX_RED,
          material: 'render' },
        { color: WALLS.RENDER_DUCKEGG, roofColor: ROOFS.SLATE, paint: PAINTS.NAVY,
          material: 'render' },
      ] },
    // Deepened to 116 so its patio meets the kerb at y = 152. At 84 it stopped
    // 32 short and left a strip of bare ground between the forecourt and the
    // footway — the only house in town whose door did not open onto pavement.
    //
    // Held 24 clear of the alley on its west flank, which is more than the 20
    // its patio leaves to the south, so `frontFacing` keeps its door on the
    // high street. Fronting the alley instead would turn its ridge east–west
    // and put a roof slope straight at the camera: the bays would be cut across
    // y, and a 146-wide plot cannot give one deeper than it is wide.
    { x: 668, y: 16, w: 122, h: 116, tall: 69, patio: true,
      tones: [
        { color: WALLS.RENDER_DUCKEGG, roofColor: ROOFS.SLATE_DARK, paint: PAINTS.NAVY,
          material: 'render' },
        { color: WALLS.RENDER_BUTTER, roofColor: ROOFS.TILE, paint: PAINTS.MUSTARD,
          material: 'render' },
        { color: WALLS.BRICK, roofColor: ROOFS.SLATE, paint: PAINTS.RACING_GREEN,
          material: 'brick' },
      ] },
    // Set back 20 for its patio (would otherwise reach y+h = 152).
    { x: 828, y: 16, w: 116, h: 116, tall: 72, patio: true,
      tones: [
        { color: WALLS.BRICK_DEEP, roofColor: ROOFS.SLATE, paint: PAINTS.MUSTARD,
          material: 'brick' },
        { color: WALLS.RENDER_ROSE, roofColor: ROOFS.TILE, paint: PAINTS.PLUM,
          material: 'render' },
      ] },
    // Bottom-left block (park sits below these).
    { x: 40, y: 312, w: 132, h: 104, tall: 70, patio: false,
      tones: [
        { color: WALLS.RENDER_SAGE, roofColor: ROOFS.SLATE, paint: PAINTS.PLUM,
          material: 'render' },
        { color: WALLS.RENDER_CREAM, roofColor: ROOFS.TILE, paint: PAINTS.TEAL,
          material: 'render' },
      ] },
    { x: 236, y: 312, w: 68, h: 158, tall: 71, patio: false,
      tones: [
        { color: WALLS.RENDER_BUTTER, roofColor: ROOFS.TILE, paint: PAINTS.TEAL,
          material: 'render' },
      ] },
    // West of the close, fronting the high street. Moved off x=600, where it
    // stood in what is now the close's carriageway, and widened to fill its
    // block: the two streets either side of it are what make it a terrace
    // rather than a house with a gap each end.
    { x: 464, y: 312, w: 116, h: 120, tall: 71, patio: false,
      tones: [
        { color: WALLS.RENDER_ROSE, roofColor: ROOFS.SLATE_DARK,
          paint: PAINTS.RACING_GREEN, material: 'render' },
        { color: WALLS.BRICK, roofColor: ROOFS.SLATE, paint: PAINTS.POSTBOX_RED,
          material: 'brick' },
      ] },
    { x: 790, y: 312, w: 150, h: 112, tall: 69, patio: false,
      tones: [
        { color: WALLS.BRICK, roofColor: ROOFS.SLATE, paint: PAINTS.POSTBOX_RED,
          material: 'brick' },
        { color: WALLS.RENDER_CREAM, roofColor: ROOFS.TILE, paint: PAINTS.NAVY,
          material: 'render' },
        { color: WALLS.BRICK_DEEP, roofColor: ROOFS.SLATE_DARK, paint: PAINTS.MUSTARD,
          material: 'brick' },
      ] },
  ],
} as const;

/**
 * Trees — the planting that stops the town reading as an empty model village.
 *
 * Scenery, but **solid**: the trunk is a collision circle, because a tree you can
 * walk through looks broken. The canopy is not — you pass under branches.
 *
 * ## Why there are no trees on the pavement
 *
 * The obvious place for street trees is the footway, and it's the one place they
 * can't go. This camera looks almost straight down, so a canopy over the walkable
 * band would hide the walker underneath it — you'd lose the dog behind a bush.
 * So every canopy stays clear of the footways and the carriageway, which leaves
 * the block interiors and the parks.
 *
 * In practice that still reads as a leafy town: trees sit at the *back edge* of
 * the block interiors, right behind the pavement, so from the street they look
 * like they're lining it. `CANOPY_RADIUS` is the envelope the clearance test
 * holds them to (`treeClearance.test.ts`) — widen it and trees start overhanging
 * the pavement, so re-run that test rather than eyeballing it.
 */
export const TREES = {
  TRUNK_RADIUS: 4.5, // also the collision circle
  TRUNK_HEIGHT: 22, // clear trunk below the canopy
  /**
   * Outer envelope of the foliage at scale 1. Everything the canopy builder
   * produces must fit inside this — see systems/trees.ts, which is unit-tested
   * against exactly that, since it's what the clearance guarantee rests on.
   */
  CANOPY_RADIUS: 17,
  BARK_COLOR: FOLIAGE.BARK,
  LEAF_COLORS: FOLIAGE.LEAF,
  /**
   * How far the foliage shades from crown to underskirt, either side of the
   * tree's own leaf colour (systems/trees.ts → blobShade).
   *
   * This is the trees' version of the rule every character in the game follows:
   * from straight above, a single-colour rounded mass is a featureless lump. A
   * canopy is a pile of balls in one green, so without a tonal top and bottom it
   * reads as a blot. ↑ and the tree starts looking lit from below.
   */
  LEAF_SHADE_SPREAD: 0.17,
  /**
   * Bark relief on the trunk, and the world height of one tile of it.
   *
   * The trunk is the one surface in the town whose pattern runs *up* rather than
   * around — see render/textures.ts. Trees are the most repeated prop in the
   * game after the windows, and a smooth trunk beside a coursed brick wall was
   * the clearest sign the scenery hadn't had the same care as the houses.
   */
  BARK_CELL: 20,
  BARK_RELIEF: 0.5,
  /**
   * The root flare: how much wider than the trunk the base spreads, and how far
   * up it reaches.
   *
   * A trunk that meets the ground as a plain cylinder looks pushed into it like
   * a peg. Real trunks splay where they land, and at this camera that splay is
   * the part you actually see — the base of the tree is nearer the bottom of the
   * screen and less often hidden by its own canopy than the trunk above it.
   */
  ROOT_FLARE: 1.7,
  ROOT_FLARE_HEIGHT: 0.28,
  /**
   * Per-silhouette proportions, multiplying the trunk and canopy sizes above.
   * The *shape* of each kind — how its foliage stacks — lives in
   * systems/trees.ts; these are just how big and how leggy it stands.
   *
   * A tall tree carries its foliage higher, and at this camera tilt that pushes
   * its canopy further north of the trunk on screen, so the tall ones need more
   * room to the north of them. treeClearance.test.ts works that out from the
   * real geometry rather than trusting these numbers.
   */
  KINDS: {
    round: { trunk: 1.0, girth: 1.0, canopy: 1.0 },
    tall: { trunk: 0.95, girth: 0.75, canopy: 0.82 }, // slim, crown does the height
    bushy: { trunk: 0.72, girth: 1.25, canopy: 1.0 }, // squat and broad
  },
  /**
   * Hand-placed, like BUILDINGS.LIST. `scale` multiplies every dimension above,
   * so the smaller ones tuck into gaps a full-size tree wouldn't clear.
   * Positions are the trunk centre on the ground plane.
   */
  LIST: [
    // Top-left block: the open forecourt around the bin.
    { x: 62, y: 52, scale: 1.0, kind: 'bushy' },
    { x: 196, y: 58, scale: 0.85, kind: 'round' },
    { x: 56, y: 118, scale: 0.9, kind: 'tall' },
    { x: 212, y: 130, scale: 1.0, kind: 'round' },
    // Top-right block: tucked into the gaps between the terrace.
    // The gap these two stood in is the alley now, so they have moved down to
    // the green at the head of the close.
    { x: 809, y: 136, scale: 0.7, kind: 'tall' },
    // Bottom-left block, and the park below it.
    { x: 210, y: 374, scale: 0.95, kind: 'round' },
    { x: 70, y: 478, scale: 1.0, kind: 'bushy' },
    { x: 152, y: 556, scale: 0.9, kind: 'round' },
    { x: 58, y: 574, scale: 0.85, kind: 'tall' },
    { x: 252, y: 556, scale: 1.0, kind: 'bushy' },
    // Bottom-right block: the green in front of home, and the side yards.
    { x: 540, y: 470, scale: 0.8, kind: 'round' },
    { x: 512, y: 508, scale: 0.95, kind: 'tall' },
    // West of the close, on the green in front of home. The trees that briefly
    // stood east of here went when the close was run down to the map edge —
    // that ground is its carriageway now.
    { x: 534, y: 596, scale: 0.9, kind: 'bushy' },
    { x: 768, y: 455, scale: 1.0, kind: 'bushy' },
    { x: 928, y: 472, scale: 0.9, kind: 'tall' },
  ],
} as const;

/**
 * Roads — quiet English-city streets running across the map. They're mostly
 * scenery; the danger is the occasional car (see TRAFFIC). A crossroads means the
 * player must cross a street now and then while managing the dog.
 */
export const ROADS = {
  WIDTH: 64, // road thickness (two lanes)
  /**
   * The three sizes of street a generated town is built from.
   *
   * A town where every street is the same width reads as a grid rather than as
   * a place — there is no high street to find your way back to and no back
   * lane to cut down. These are the hierarchy, and they are deliberately only
   * three: a road's width is read at a glance from almost overhead, and any
   * finer gradation is a difference nobody can see.
   *
   * `STREET` is `WIDTH` exactly, so an ordinary street is what every street
   * always was and the hand-authored town is untouched.
   *
   * **`HIGH` is a difficulty change, not just a look.** The crossing window
   * (`levelgen/difficulty.ts` → `crossingWindowMs`) is how long a walker needs
   * to get across a carriageway with a dog hauling backwards, and it scales
   * with this — so a wide high street demands a correspondingly longer gap in
   * the traffic, and crossing it is meant to be the frightening one. Widen this
   * and the validator will start throwing away seeds whose traffic can't leave
   * room for it, which is the system working rather than failing.
   *
   * `BACK` is held well above the walker's diameter with the dog beside them:
   * it is a narrower carriageway, not a narrower *route*, and a street you
   * cannot get across is not a shortcut.
   */
  WIDTHS: {
    HIGH: 96,
    STREET: 64,
    BACK: 48,
  },
  COLOR: GROUND_COLORS.TARMAC,
  MARKING_COLOR: GROUND_COLORS.ROAD_MARKING, // dashed centre line + stop lines
  DASH_LENGTH: 20, // length of one centre-line dash
  DASH_SPACING: 40, // dash pitch, so a dash covers half the gap
  // Stop lines at each junction approach. The setback keeps them just clear of
  // the junction square rather than sitting on its edge.
  /**
   * Aggregate speckle on the tarmac: the world size of one tile of it, and how
   * much relief it carries.
   *
   * The road is the widest surface in the town and runs its whole length, so the
   * cell is large and the pattern deliberately shapeless — see
   * render/textures.ts. Relief is the lowest in the game: tarmac is rough, but
   * it is also nearly flat to this camera, and a bumpy road reads as gravel.
   */
  AGGREGATE_CELL: 96,
  AGGREGATE_RELIEF: 0.18,

  // ── Paint ───────────────────────────────────────────────────────────────
  // Crossings, kerb lines and drains. All of it is flat, so none of it can be
  // walked into — which is precisely why the streets get their character this
  // way rather than from bollards and lamp posts. See systems/markings.ts.
  /**
   * How far a zebra sits back from the middle of the junction it serves.
   *
   * Bounded at both ends, and `markings.test.ts` holds both: past
   * `WIDTH / 2` or the stripes are painted across live turning traffic, and
   * past the far kerb they end up on the pavement.
   */
  CROSSING_SETBACK: 46,
  CROSSING_DEPTH: 26, // how far the crossing reaches along the road
  CROSSING_STRIPE: 7, // one white bar; gaps match, so the pitch is twice this
  /** Double yellows: in from the kerb, apart from each other, and how thick. */
  KERB_LINE_INSET: 4,
  KERB_LINE_GAP: 4,
  KERB_LINE_WIDTH: 2,
  KERB_LINE_COLOR: GROUND_COLORS.KERB_LINE,
  /** Gully drains along the kerb, and how big each cover is. */
  DRAIN_SPACING: 150,
  DRAIN_SIZE: 9,
  DRAIN_COLOR: GROUND_COLORS.DRAIN,
  STOP_LINE_WIDTH: 4,
  STOP_LINE_SETBACK: 3,
  /**
   * The town's ways. 'h' = horizontal at y=pos, 'v' = vertical at x=pos.
   *
   * `from`/`to` bound a way along its own axis and are absent for one that runs
   * clean across the map; `kind: 'lane'` is a cobbled pedestrian alley with no
   * carriageway. See `systems/roads.ts` for the resolvers that apply those
   * defaults, and `levelgen/town.ts` for how a generated town uses them.
   *
   * Two of these run the full map and two do not, which is the shape of the
   * whole town:
   *
   * - **The high street and the cross** carry all the traffic. Only a street
   *   running edge to edge can: a car has to come from somewhere and go
   *   somewhere, and one appearing halfway down a street reads worse than an
   *   empty street. `authoredTraffic()` derives that rather than listing it.
   * - **The close** runs south off the high street to the bottom of the map, and
   *   **the alley** cuts between the terraces along the top. Both are cobbled: a
   *   pavement wide, walkable end to end, and no cars ever.
   *
   * Neither of the two cobbled ways runs the whole map, which is what makes the
   * blocks uneven — a way that starts halfway divides nothing above its start.
   * Both still *go* somewhere: each ends at a street or at the map edge, never
   * in open ground. A street is the one thing a town cannot have stopping in
   * the middle of nowhere, and making these lanes rather than carriageways is
   * also what keeps the errand across two roads rather than three — the
   * authored town is the fixed point the difficulty bands are cut against, and
   * it should not sit on a boundary.
   */
  SEGMENTS: [
    { orient: 'h', pos: 232 },
    { orient: 'v', pos: 384 },
    { orient: 'v', pos: 660, from: 232, kind: 'lane' },
    { orient: 'v', pos: 620, from: 0, to: 152, kind: 'lane' },
  ],
} as const;

/**
 * Traffic — an occasional hazard. A vehicle touching the player OR dog ends the
 * run, so everything here is felt as difficulty as well as seen.
 */
export const VEHICLES = {
  /**
   * Shared across every kind, deliberately. Width is what decides whether a
   * vehicle sits inside its lane, and the lanes are derived from `ROADS.WIDTH`
   * (see systems/roads.ts) rather than from anything here — so varying it per
   * kind would mean re-checking lane clearance for each one to buy a difference
   * you can barely see from above.
   */
  WIDTH: 22,
  SPEED: 170,
  TINTS: PROPS.CAR_TINTS,
  /**
   * Per-kind proportions. The *shape* of each — how the greenhouse is built on
   * top — lives in render/models.ts; these are the box it has to fit.
   *
   * `DECK` is the top of the hull: the bonnet line, and the height everything
   * bolted to the body (bumpers, lamps, mirrors) hangs off. It barely differs
   * between the two, because a van's bonnet isn't much higher than a car's —
   * what makes a van a van is the box *behind* the cab, not a taller nose.
   *
   * `HEIGHT` is the roof, and `models.test.ts` holds each kind's model to
   * landing exactly on it. Keep vans modest: at this camera tilt anything tall
   * is drawn about 0.63 × its height toward the top of the screen (see
   * AGENTS.md), so a genuinely box-shaped van starts covering the pavement it
   * is driving past — and the walker standing on it.
   */
  KINDS: {
    car: { LENGTH: 42, HEIGHT: 18, DECK: 13 },
    van: { LENGTH: 54, HEIGHT: 24, DECK: 14 },
  },
  /** Longest of the above — spawn/despawn margins have to clear every kind. */
  MAX_LENGTH: 54,
  // ── The horn ───────────────────────────────────────────────────────────────
  // A vehicle sounds off when it is *about to hit someone*, which is a much
  // narrower thing than "is nearby": the walker or the dog has to be inside
  // HORN_RADIUS **and** in front of it (systems/horn.ts). A car that has
  // already gone past stays quiet, so the beep is only ever a warning you can
  // still act on.
  //
  // The radius is roughly a second of travel at SPEED — any shorter and the
  // horn arrives after the impact it was warning about.
  HORN_RADIUS: 170,
  // How far off dead-ahead still counts, as the cosine of the half-angle. 0.35
  // is a ~70° cone either side: wide enough to cover someone stepping off the
  // kerb, tight enough to stay silent for the pavement behind the car.
  HORN_CONE: 0.35,
  // One beep per vehicle per this long, so a slow crossing isn't a fanfare.
  HORN_COOLDOWN_MS: 1400,
  // ── Junction turns & blinkers ──────────────────────────────────────────────
  // Probability a vehicle turns when approaching a junction with open branches.
  TURN_CHANCE: 0.35,
  /**
   * How long a vehicle indicates *before* it starts turning, in milliseconds.
   *
   * A **time**, not a distance, and that is the whole point. This used to be a
   * flat 120 units, which at `SPEED` is 0.7 s — under three flashes at
   * `BLINKER_INTERVAL_MS`, of which the lamp is actually lit for about half.
   * That reads as a car turning without signalling, because by the time you
   * have registered an orange dot the car is already on the arc.
   *
   * Two seconds is roughly what a real driver gives, and it survives the speed
   * changing: the notice distance is derived from the vehicle's own speed
   * (`noticeDistance()`), so bumping `SPEED` can no longer quietly shrink the
   * warning back to nothing.
   *
   * Note this is a *ceiling*, not a promise. The signal never starts before the
   * junction the vehicle will actually turn at is the next one it meets, so on
   * a short block between two crossings the warning is however long that block
   * gives — see `nearestJunctionAhead`. Indicating through a junction you are
   * driving straight over would be worse than indicating late.
   */
  BLINKER_LEAD_MS: 2000,
  // Blinker flash period (ms).
  BLINKER_INTERVAL_MS: 250,
} as const;

/** How often traffic appears. Kept sparse so it surprises rather than dominates. */
export const TRAFFIC = {
  SPAWN_MIN_MS: 4500, // earliest gap between spawns
  SPAWN_JITTER_MS: 6000, // plus up to this much extra
  MAX_VEHICLES: 2, // never more than this on screen at once
  FIRST_SPAWN_MS: 3000, // grace period before the first vehicle
  /**
   * How often a spawn is a van rather than a car. A third: enough that you meet
   * them, few enough that the bigger, deadlier footprint stays a surprise.
   */
  VAN_SHARE: 0.34,
} as const;
